/**
 * What this workspace has, according to the server.
 *
 * The one rule this file exists to hold: **the frontend does not decide.**
 * Whether a product is entitled, whether it has been initialized, how its
 * setup is going and what to offer next are all answered by
 * `GET /businesses/:id/platform/products`, and everything here is a
 * translation of that answer into words and a button.
 *
 * Re-deriving any of it in the browser would mean two implementations of a
 * rule that reads three facts, and the copy is always the one that eventually
 * offers "Open" for something still provisioning. So there is no
 * `if (entitled && activated)` anywhere in the UI — there is a `switch` on the
 * action the server already chose.
 */

import { useQuery } from '@tanstack/react-query';
import type { ProductAction, ProvisioningStatus } from '@cenox/shared';
import { api } from './api';
import { useAuth, useBusinessPath } from './auth';
import { NO_WORKSPACE_MESSAGE } from '../components/platform/NoWorkspaceNotice';

export interface PlatformProduct {
  product: string;
  name: string;
  description: string | null;
  routeSlug: string;
  /** `available` is sellable today; `coming_soon` is listed and not sold. */
  catalogueStatus: string;

  entitled: boolean;
  activated: boolean;
  provisioningStatus: ProvisioningStatus | null;
  availableAction: ProductAction;

  enabled: boolean;
  activatedAt: string | null;
  /**
   * A code from a fixed vocabulary, and a sentence written for a person.
   * The detailed reason stays on the server; this is all that is published.
   */
  failureCode: string | null;
  failureMessage: string | null;
  provisionable: boolean;
}

/**
 * How each action is presented, and what pressing it does.
 *
 * `kind` matters as much as the label: only `activate` sends anything to the
 * server. Rendering Home must never provision a product — a screen that
 * initialized things by being looked at would seed a workspace the moment
 * somebody opened the wrong tab.
 */
export const PRODUCT_ACTIONS: Record<
  ProductAction,
  { label: string; kind: 'navigate' | 'activate' | 'none'; variant: 'primary' | 'secondary' | 'ghost' }
> = {
  open: { label: 'Open', kind: 'navigate', variant: 'primary' },
  activate: { label: 'Activate', kind: 'activate', variant: 'primary' },
  reactivate: { label: 'Reactivate', kind: 'activate', variant: 'primary' },
  wait: { label: 'Provisioning', kind: 'none', variant: 'secondary' },
  retry: { label: 'Retry setup', kind: 'activate', variant: 'secondary' },
  start_trial: { label: 'Start trial', kind: 'navigate', variant: 'primary' },
  upgrade: { label: 'Get access', kind: 'navigate', variant: 'secondary' },
  unavailable: { label: 'Coming soon', kind: 'none', variant: 'ghost' },
  contact_support: { label: 'Contact support', kind: 'none', variant: 'secondary' },
};

/**
 * The line under each product name.
 *
 * Reads the server's fields; it does not recompute them. `failureMessage` is
 * already safe to show — the provider's actual response never leaves the
 * server — so it is used verbatim rather than re-worded here.
 */
export function statusLine(p: PlatformProduct, hasWorkspace = true): string {
  if (p.provisioningStatus === 'failed') {
    return p.failureMessage ?? 'Setting this product up did not finish.';
  }
  if (p.provisioningStatus === 'pending' || p.provisioningStatus === 'provisioning') {
    return 'Setting up. This can take a moment.';
  }
  if (p.entitled && p.activated && !p.enabled) return 'Switched off for this workspace.';
  if (p.entitled && p.activated) return 'Ready to use.';
  if (p.entitled) return 'Included in this workspace. Not set up yet.';
  if (p.catalogueStatus !== 'available') return 'Not available yet.';
  /*
    Somebody with no workspace was never in one. Telling them a product is
    "not part of this workspace" reads as though they were removed from it.
  */
  if (!hasWorkspace) return NO_WORKSPACE_MESSAGE;
  return 'Not part of this workspace.';
}

/**
 * Where a product lives.
 *
 * Its own slug, from the server, rather than a map in the browser — so a
 * product renamed in the catalogue moves without a deploy.
 */
export const productHref = (p: PlatformProduct): string => `/${p.routeSlug}`;

export function usePlatformProducts() {
  const { user, business } = useAuth();
  const path = useBusinessPath();

  /**
   * Somebody with no workspace still has an ecosystem to look at.
   *
   * A CenoX account exists before any workspace does, so the list comes from
   * the tenant-free catalogue instead — every product locked, because a person
   * who holds nothing holds nothing. Without this the query would sit disabled
   * forever and every screen reading it would spin.
   */
  const workspace = useQuery({
    queryKey: ['platform-products', business?.businessId],
    queryFn: () => api.get<{ data: PlatformProduct[] }>(path('/platform/products')),
    enabled: Boolean(business),
    /**
     * A product that is provisioning will finish without anybody pressing
     * anything, so the screen has to notice on its own. Only while something
     * is actually in flight — polling a settled list forever would be a
     * request every few seconds for a page that never changes.
     */
    refetchInterval: (query) => {
      const rows = query.state.data?.data ?? [];
      const busy = rows.some(
        (p) => p.provisioningStatus === 'pending' || p.provisioningStatus === 'provisioning',
      );
      return busy ? 4000 : false;
    },
  });

  const catalogue = useQuery({
    queryKey: ['platform-catalogue'],
    queryFn: () => api.get<{ data: PlatformProduct[] }>('/platform/catalogue'),
    enabled: Boolean(user) && !business,
    staleTime: 5 * 60_000,
  });

  return business ? workspace : catalogue;
}
